// Chapter 3: Introduction to Federal Multi-Peril Crop Insurance
// Comprehensive coverage of MPCI fundamentals

const chapter3 = {
  id: 3,
  title: "Introduction to Federal Multi-Peril Crop Insurance",
  sections: [
    {
      title: "Overview of Federal Crop Insurance",
      content: `
        <h2>Overview of Federal Crop Insurance</h2>

        <p>Federal Multi-Peril Crop Insurance (MPCI) is a comprehensive crop insurance program administered by the Risk Management Agency (RMA) of the USDA. Unlike private crop hail insurance, MPCI provides protection against multiple perils and is heavily subsidized by the federal government.</p>

        <div class="key-concept">
          <h3>Key Concept: What is MPCI?</h3>
          <p><strong>Multi-Peril Crop Insurance (MPCI)</strong> covers losses from multiple causes including drought, excess moisture, hail, wind, frost, insects, and disease. It provides broader protection than single-peril policies like crop hail insurance.</p>
        </div>

        <h3>Key Differences: MPCI vs. Crop Hail Insurance</h3>
        <table>
          <tr>
            <th>Feature</th>
            <th>MPCI</th>
            <th>Crop Hail</th>
          </tr>
          <tr>
            <td>Perils Covered</td>
            <td>Multiple (drought, flood, hail, frost, etc.)</td>
            <td>Hail and fire only (typically)</td>
          </tr>
          <tr>
            <td>Premium Subsidy</td>
            <td>Yes - Government subsidized</td>
            <td>No - Full premium paid by farmer</td>
          </tr>
          <tr>
            <td>Regulation</td>
            <td>Federal (RMA/FCIC)</td>
            <td>State regulated</td>
          </tr>
          <tr>
            <td>Policy Forms</td>
            <td>Standardized by FCIC</td>
            <td>NCIS standard forms (may vary)</td>
          </tr>
          <tr>
            <td>Sales Deadline</td>
            <td>Fixed by RMA</td>
            <td>Generally until crop emerges</td>
          </tr>
        </table>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Remember: MPCI is federally regulated with government premium subsidies, while crop hail is state regulated with no subsidies. This is a fundamental distinction the exam will test.</p>
        </div>
      `
    },
    {
      title: "History of Federal Crop Insurance",
      content: `
        <h2>History of Federal Crop Insurance</h2>

        <p>Understanding the evolution of federal crop insurance helps explain the current program structure and why certain rules exist.</p>

        <h3>Key Legislative Milestones</h3>

        <div class="definition">
          <h4>Federal Crop Insurance Act of 1938</h4>
          <p>Created the Federal Crop Insurance Corporation (FCIC) and established the first permanent federal crop insurance program. Initially limited to wheat in selected counties.</p>
        </div>

        <div class="definition">
          <h4>Federal Crop Insurance Act of 1980</h4>
          <p>Major expansion that introduced the public-private partnership model. Private insurance companies (AIPs) began selling and servicing federal crop insurance policies. Premium subsidies were introduced to encourage participation.</p>
        </div>

        <div class="definition">
          <h4>Federal Crop Insurance Reform Act of 1994</h4>
          <p>Made catastrophic (CAT) coverage mandatory for farmers receiving certain USDA benefits. Established the current structure of coverage levels.</p>
        </div>

        <div class="definition">
          <h4>Agricultural Risk Protection Act (ARPA) of 2000</h4>
          <p>Significantly increased premium subsidies, introduced new insurance products, and strengthened program integrity measures.</p>
        </div>

        <div class="key-concept">
          <h3>Key Concept: Public-Private Partnership</h3>
          <p>The federal crop insurance program operates through a <strong>public-private partnership</strong>:</p>
          <ul>
            <li><strong>Public (Government):</strong> FCIC develops policies, sets rates, provides premium subsidies, and offers reinsurance</li>
            <li><strong>Private (AIPs):</strong> Approved Insurance Providers sell policies, collect premiums, service policies, and process claims</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>The 1980 Act is particularly important - it created the modern delivery system with AIPs and premium subsidies. This is when crop insurance became widely accessible to farmers.</p>
        </div>
      `
    },
    {
      title: "Key Organizations in Federal Crop Insurance",
      content: `
        <h2>Key Organizations in Federal Crop Insurance</h2>

        <p>Several organizations play critical roles in the federal crop insurance system. Understanding their relationships is essential.</p>

        <div class="definition">
          <h4>Federal Crop Insurance Corporation (FCIC)</h4>
          <p>A government corporation within USDA that administers the federal crop insurance program. FCIC:</p>
          <ul>
            <li>Develops and approves crop insurance policies</li>
            <li>Sets premium rates</li>
            <li>Provides premium subsidies</li>
            <li>Offers reinsurance to AIPs</li>
            <li>Establishes program rules and procedures</li>
          </ul>
        </div>

        <div class="definition">
          <h4>Risk Management Agency (RMA)</h4>
          <p>The USDA agency that manages FCIC and administers federal crop insurance programs. RMA:</p>
          <ul>
            <li>Oversees all aspects of program administration</li>
            <li>Conducts compliance reviews</li>
            <li>Manages the Standard Reinsurance Agreement (SRA)</li>
            <li>Provides training and education</li>
          </ul>
        </div>

        <div class="definition">
          <h4>Approved Insurance Providers (AIPs)</h4>
          <p>Private insurance companies authorized to sell and service federal crop insurance policies. AIPs:</p>
          <ul>
            <li>Market and sell policies to farmers</li>
            <li>Collect premiums</li>
            <li>Process and pay claims</li>
            <li>Maintain records and documentation</li>
            <li>Share risk with FCIC through the SRA</li>
          </ul>
        </div>

        <div class="key-concept">
          <h3>Key Concept: Standard Reinsurance Agreement (SRA)</h3>
          <p>The SRA is a contract between FCIC and each AIP that:</p>
          <ul>
            <li>Defines the terms of the public-private partnership</li>
            <li>Establishes how risk is shared between AIPs and FCIC</li>
            <li>Sets administrative and operating expense reimbursements</li>
            <li>Specifies compliance requirements</li>
          </ul>
        </div>

        <div class="example">
          <h4>Example: How the System Works Together</h4>
          <p>A South Dakota farmer wants crop insurance:</p>
          <ol>
            <li>The farmer contacts an AIP (or agent representing an AIP)</li>
            <li>The AIP sells a policy using FCIC-approved forms and rates</li>
            <li>FCIC subsidizes a portion of the premium</li>
            <li>If a loss occurs, the AIP processes the claim</li>
            <li>FCIC shares in the loss through the SRA reinsurance</li>
          </ol>
        </div>
      `
    },
    {
      title: "Types of MPCI Coverage",
      content: `
        <h2>Types of MPCI Coverage</h2>

        <p>MPCI offers several types of coverage to meet different producer needs. The main categories are yield-based and revenue-based protection.</p>

        <h3>Yield Protection (YP)</h3>
        <div class="definition">
          <p><strong>Yield Protection</strong> insures against production losses only. The guarantee is based on the producer's Actual Production History (APH) yield times the coverage level selected.</p>
          <p><strong>Indemnity Trigger:</strong> Actual yield falls below the guaranteed yield</p>
        </div>

        <h3>Revenue Protection (RP)</h3>
        <div class="definition">
          <p><strong>Revenue Protection</strong> insures against loss of revenue due to price decline, yield loss, or a combination of both. The guarantee can increase if the harvest price exceeds the projected price.</p>
          <p><strong>Indemnity Trigger:</strong> Actual revenue falls below the guaranteed revenue</p>
        </div>

        <h3>Revenue Protection with Harvest Price Exclusion (RP-HPE)</h3>
        <div class="definition">
          <p><strong>RP-HPE</strong> is similar to Revenue Protection but the guarantee does NOT increase if harvest price exceeds projected price. This results in lower premiums than standard RP.</p>
        </div>

        <h3>Catastrophic Coverage (CAT)</h3>
        <div class="definition">
          <p><strong>CAT Coverage</strong> is the minimum level of coverage available, providing 50% of the approved yield at 55% of the price. Farmers pay only an administrative fee (no premium).</p>
        </div>

        <div class="key-concept">
          <h3>Key Concept: Coverage Levels</h3>
          <p>Above CAT level, producers can select coverage levels typically ranging from:</p>
          <ul>
            <li><strong>50% to 85%</strong> of approved yield or revenue (in 5% increments)</li>
            <li>Higher coverage levels = higher premiums but more protection</li>
            <li>Premium subsidy percentages decrease at higher coverage levels</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know the difference between RP and RP-HPE: Standard RP can have an INCREASING guarantee if harvest price rises; RP-HPE cannot. This is why RP costs more.</p>
        </div>

        <table>
          <tr>
            <th>Coverage Type</th>
            <th>Protects Against</th>
            <th>Price Component</th>
          </tr>
          <tr>
            <td>Yield Protection (YP)</td>
            <td>Yield loss only</td>
            <td>Fixed projected price</td>
          </tr>
          <tr>
            <td>Revenue Protection (RP)</td>
            <td>Yield and/or price loss</td>
            <td>Higher of projected or harvest price</td>
          </tr>
          <tr>
            <td>RP-HPE</td>
            <td>Yield and/or price loss</td>
            <td>Projected price only</td>
          </tr>
          <tr>
            <td>CAT</td>
            <td>Catastrophic yield loss</td>
            <td>55% of price, 50% of yield</td>
          </tr>
        </table>
      `
    },
    {
      title: "Actual Production History (APH)",
      content: `
        <h2>Actual Production History (APH)</h2>

        <p>The Actual Production History (APH) system is the foundation of most MPCI coverage. It establishes the producer's expected yield based on their historical production records.</p>

        <div class="definition">
          <h4>What is APH?</h4>
          <p><strong>APH</strong> is the average of a producer's actual yields over a base period (typically 4-10 years), used to calculate the production guarantee for crop insurance.</p>
        </div>

        <h3>APH Calculation Basics</h3>
        <ul>
          <li><strong>Base Period:</strong> Minimum of 4 years, maximum of 10 years of production history</li>
          <li><strong>Calculation:</strong> Sum of annual yields ÷ Number of years = APH Yield</li>
          <li><strong>Updates:</strong> APH is updated annually as new production data becomes available</li>
        </ul>

        <div class="example">
          <h4>Example: APH Calculation</h4>
          <p>A producer has the following corn yields over 5 years:</p>
          <ul>
            <li>Year 1: 150 bu/acre</li>
            <li>Year 2: 145 bu/acre</li>
            <li>Year 3: 160 bu/acre</li>
            <li>Year 4: 130 bu/acre (drought year)</li>
            <li>Year 5: 155 bu/acre</li>
          </ul>
          <p><strong>APH = (150 + 145 + 160 + 130 + 155) ÷ 5 = 148 bu/acre</strong></p>
        </div>

        <h3>Transitional Yields (T-Yields)</h3>
        <div class="definition">
          <p>When a producer lacks sufficient production history, <strong>Transitional Yields (T-Yields)</strong> may be used. T-Yields are county-based yields established by RMA, typically set at a percentage of the county average.</p>
        </div>

        <h3>Yield Floors and Cups</h3>
        <div class="key-concept">
          <h3>Key Concept: Yield Substitution</h3>
          <p>To prevent APH from declining too rapidly after disaster years:</p>
          <ul>
            <li><strong>Yield Floor:</strong> Prevents APH from dropping below a certain level</li>
            <li><strong>Yield Cup:</strong> Allows substitution of a higher yield when actual production is low due to insured causes</li>
          </ul>
          <p>These provisions help producers maintain reasonable guarantees after loss years.</p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Remember: APH requires MINIMUM 4 years of history. If a producer has fewer years, T-Yields fill the gap. APH is updated each year with new production data.</p>
        </div>
      `
    },
    {
      title: "Approved APH Yields",
      content: `
        <h2>Approved APH Yields</h2>

        <p>The Approved APH Yield is the yield used to calculate a producer's guarantee. Understanding how it's determined is crucial.</p>

        <h3>Components of Approved Yield</h3>

        <div class="definition">
          <h4>Actual Yields</h4>
          <p>Production records from the producer's own farming operation, verified through acceptable documentation (sales records, measurement services, etc.).</p>
        </div>

        <div class="definition">
          <h4>Assigned Yields</h4>
          <p>Yields assigned when production records are missing or unavailable. May be reduced to 65% or 80% of T-Yield depending on circumstances.</p>
        </div>

        <div class="definition">
          <h4>Transitional Yields</h4>
          <p>County-based yields used to complete the database when producer lacks sufficient history. Calculated as a percentage of county reference yield.</p>
        </div>

        <h3>Yield Adjustment Factors</h3>

        <div class="key-concept">
          <h3>Key Concept: Yield Adjustments</h3>
          <p>Several factors can adjust the approved yield:</p>
          <ul>
            <li><strong>Simple Average:</strong> Basic calculation using available yields</li>
            <li><strong>Yield Exclusion (YE):</strong> Allows exclusion of certain low-yield years caused by widespread disasters</li>
            <li><strong>Trend Adjustment:</strong> Some crops allow upward adjustment to reflect improving production technology</li>
            <li><strong>Plug Yields:</strong> Replace low yields to stabilize APH</li>
          </ul>
        </div>

        <h3>Documentation Requirements</h3>
        <p>Acceptable production evidence includes:</p>
        <ul>
          <li>Commercial sales receipts (elevator, processor)</li>
          <li>Settlement sheets</li>
          <li>FSA records (Form 578)</li>
          <li>Warehouse receipts</li>
          <li>Measurement services</li>
          <li>Feed records (for farm-stored production)</li>
        </ul>

        <div class="important">
          <h3>Important: Production Verification</h3>
          <p>All production must be verifiable. Unsubstantiated claims for production can result in:</p>
          <ul>
            <li>Yield not being counted</li>
            <li>Voided policies</li>
            <li>Potential fraud investigation</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>The exam may test your knowledge of acceptable production documentation. Sales receipts and FSA records are always acceptable; personal estimates are NOT acceptable.</p>
        </div>
      `
    },
    {
      title: "Unit Structure",
      content: `
        <h2>Unit Structure</h2>

        <p>The "unit" is the basic building block for MPCI coverage. How units are structured significantly affects premiums and loss determination.</p>

        <div class="definition">
          <h4>What is a Unit?</h4>
          <p>A <strong>unit</strong> is all insurable acreage of a crop in a county in which the insured has a share. Units determine how losses are calculated and how premiums are charged.</p>
        </div>

        <h3>Types of Units</h3>

        <div class="definition">
          <h4>Basic Unit</h4>
          <p>All acreage of the insured crop in the county in which the insured has 100% share, plus all acreage that is owned and rented.</p>
        </div>

        <div class="definition">
          <h4>Optional Unit</h4>
          <p>Allows the insured to divide acreage into smaller units by section or FSA farm serial number. Requirements:</p>
          <ul>
            <li>Land must be in separate sections OR have separate FSA FSN</li>
            <li>Must maintain separate production records by unit</li>
            <li>Slightly higher premium than enterprise units</li>
          </ul>
        </div>

        <div class="definition">
          <h4>Enterprise Unit</h4>
          <p>Combines all acreage of a crop in a county into one unit. Benefits:</p>
          <ul>
            <li>Significant premium discount (typically 15-20%)</li>
            <li>Simpler recordkeeping</li>
            <li>Trade-off: Must suffer loss across entire unit to collect</li>
          </ul>
        </div>

        <div class="definition">
          <h4>Whole Farm Unit</h4>
          <p>Combines multiple crops into a single unit for revenue protection purposes.</p>
        </div>

        <div class="example">
          <h4>Example: Unit Structure Impact</h4>
          <p>A producer farms 1,000 acres of corn across 4 sections:</p>
          <ul>
            <li><strong>Enterprise Unit:</strong> All 1,000 acres in one unit - lowest premium, but hail on one section may not trigger payment if other sections do well</li>
            <li><strong>Optional Units:</strong> 4 separate units of 250 acres each - higher premium, but loss on one section can trigger payment for that unit</li>
          </ul>
        </div>

        <div class="key-concept">
          <h3>Key Concept: Loss Calculation by Unit</h3>
          <p>Losses are calculated at the UNIT level, not the field level. A producer with optional units can collect an indemnity on one unit while another unit has no loss. With an enterprise unit, all acreage is combined before determining if a loss occurred.</p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Enterprise units have the lowest premium but highest threshold to collect. Optional units cost more but can pay on localized losses. Know the trade-offs!</p>
        </div>
      `
    },
    {
      title: "Important Dates and Deadlines",
      content: `
        <h2>Important Dates and Deadlines</h2>

        <p>MPCI has strict deadlines that must be followed. Missing a deadline can result in denied coverage or claims. These dates are set by RMA and vary by crop and location.</p>

        <h3>Key Dates in the Crop Insurance Calendar</h3>

        <div class="definition">
          <h4>Sales Closing Date (SCD)</h4>
          <p>The last date to apply for coverage, make changes to an existing policy, or cancel a policy. Missing this date means NO coverage for that crop year.</p>
        </div>

        <div class="definition">
          <h4>Production Reporting Date (PRD)</h4>
          <p>The deadline to submit the previous year's production records to update APH. Late filing may result in assigned yields being used.</p>
        </div>

        <div class="definition">
          <h4>Acreage Reporting Date (ARD)</h4>
          <p>The deadline to report all planted acreage for the crop year. Must include:</p>
          <ul>
            <li>Acres planted by crop and type/variety</li>
            <li>Planting dates</li>
            <li>Share arrangement</li>
            <li>Practice (irrigated/non-irrigated)</li>
          </ul>
        </div>

        <div class="definition">
          <h4>Premium Billing Date</h4>
          <p>The date by which premium is due. Failure to pay results in policy termination and potential collection action.</p>
        </div>

        <div class="definition">
          <h4>End of Insurance Period</h4>
          <p>The date when coverage ends for the crop year. Varies by crop - generally the earlier of harvest completion or a calendar date.</p>
        </div>

        <div class="key-concept">
          <h3>Key Concept: South Dakota Key Dates</h3>
          <p>Common dates for major South Dakota crops (always verify current year dates):</p>
          <table>
            <tr>
              <th>Crop</th>
              <th>Sales Closing</th>
              <th>Acreage Reporting</th>
            </tr>
            <tr>
              <td>Corn</td>
              <td>March 15</td>
              <td>July 15</td>
            </tr>
            <tr>
              <td>Soybeans</td>
              <td>March 15</td>
              <td>July 15</td>
            </tr>
            <tr>
              <td>Spring Wheat</td>
              <td>March 15</td>
              <td>July 15</td>
            </tr>
            <tr>
              <td>Winter Wheat</td>
              <td>September 30</td>
              <td>November 15</td>
            </tr>
          </table>
        </div>

        <div class="important">
          <h3>Important: Consequences of Missing Deadlines</h3>
          <ul>
            <li><strong>Missed SCD:</strong> No coverage available for that crop year</li>
            <li><strong>Missed ARD:</strong> Acreage may be uninsured or subject to late filing fees</li>
            <li><strong>Missed PRD:</strong> Yield may be reduced or assigned yield used</li>
            <li><strong>Late Premium:</strong> Policy termination and debt collection</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>The exam will likely test your knowledge of what happens when deadlines are missed. Focus on the consequences, not memorizing specific dates (which change).</p>
        </div>
      `
    },
    {
      title: "Acreage Reporting",
      content: `
        <h2>Acreage Reporting</h2>

        <p>Accurate acreage reporting is essential for MPCI coverage. Errors or misrepresentations can void coverage or result in reduced indemnities.</p>

        <h3>What Must Be Reported</h3>
        <ul>
          <li>Total acres of each insured crop planted</li>
          <li>Location of acres (legal description or FSA identification)</li>
          <li>Crop type and variety (if applicable)</li>
          <li>Intended use (grain, silage, etc.)</li>
          <li>Planting date(s)</li>
          <li>Practice (irrigated or non-irrigated)</li>
          <li>Share percentage and sharing arrangement</li>
          <li>Prevented planting acres (if applicable)</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Acreage Determination</h3>
          <p>Acreage may be determined by:</p>
          <ul>
            <li><strong>FSA Records:</strong> Most common, based on official measurements</li>
            <li><strong>Measurement Service:</strong> Company representative measures acres</li>
            <li><strong>Precision GPS:</strong> Increasingly accepted technology</li>
            <li><strong>Producer Certification:</strong> Producer certifies acres (subject to spot-check)</li>
          </ul>
        </div>

        <h3>Acreage Discrepancies</h3>

        <div class="important">
          <h3>Important: Under-Reporting Consequences</h3>
          <p>If reported acreage is less than actual acreage, the indemnity may be reduced proportionally. This affects the loss calculation:</p>
          <p><strong>Adjusted Guarantee = Original Guarantee × (Reported Acres ÷ Determined Acres)</strong></p>
        </div>

        <div class="example">
          <h4>Example: Under-Reporting Impact</h4>
          <p>A producer reports 500 acres but actually planted 550 acres:</p>
          <ul>
            <li>Reported: 500 acres</li>
            <li>Determined: 550 acres</li>
            <li>Ratio: 500 ÷ 550 = 90.9%</li>
            <li>Guarantee is reduced to 90.9% of original amount</li>
          </ul>
          <p>This reduction applies even if the under-reporting was unintentional.</p>
        </div>

        <h3>Over-Reporting</h3>
        <p>If reported acreage exceeds actual acreage, the policy is adjusted to actual acres. The producer does NOT benefit from over-reporting.</p>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Acreage under-reporting results in proportional guarantee reduction. Over-reporting simply gets corrected to actual acres. The exam tests these consequences.</p>
        </div>
      `
    },
    {
      title: "Premium Calculation Basics",
      content: `
        <h2>Premium Calculation Basics</h2>

        <p>Understanding how MPCI premiums are calculated helps agents explain costs to producers and compare coverage options.</p>

        <h3>Basic Premium Formula</h3>
        <div class="key-concept">
          <h3>Key Concept: Premium Components</h3>
          <p><strong>Total Premium = Liability × Premium Rate</strong></p>
          <p>Where:</p>
          <ul>
            <li><strong>Liability</strong> = Acres × APH Yield × Coverage Level × Price Election</li>
            <li><strong>Premium Rate</strong> = Base rate adjusted for coverage level, unit structure, and other factors</li>
          </ul>
        </div>

        <div class="example">
          <h4>Example: Premium Calculation</h4>
          <p>A corn producer with the following:</p>
          <ul>
            <li>500 acres</li>
            <li>APH Yield: 150 bu/acre</li>
            <li>Coverage Level: 75%</li>
            <li>Price Election: $4.00/bu</li>
            <li>Premium Rate: 6%</li>
          </ul>
          <p><strong>Liability = 500 × 150 × 0.75 × $4.00 = $225,000</strong></p>
          <p><strong>Total Premium = $225,000 × 0.06 = $13,500</strong></p>
        </div>

        <h3>Premium Subsidies</h3>
        <p>The federal government subsidizes a portion of the premium. Subsidy percentages vary by coverage level:</p>

        <table>
          <tr>
            <th>Coverage Level</th>
            <th>Basic/Optional Unit Subsidy</th>
            <th>Enterprise Unit Subsidy</th>
          </tr>
          <tr>
            <td>CAT</td>
            <td>100% (admin fee only)</td>
            <td>N/A</td>
          </tr>
          <tr>
            <td>50-55%</td>
            <td>67%</td>
            <td>80%</td>
          </tr>
          <tr>
            <td>60-65%</td>
            <td>64%</td>
            <td>80%</td>
          </tr>
          <tr>
            <td>70-75%</td>
            <td>59%</td>
            <td>77%</td>
          </tr>
          <tr>
            <td>80-85%</td>
            <td>55%</td>
            <td>68%</td>
          </tr>
        </table>

        <div class="example">
          <h4>Example: Producer Premium</h4>
          <p>Using the example above with 75% coverage and basic unit (59% subsidy):</p>
          <ul>
            <li>Total Premium: $13,500</li>
            <li>Government Subsidy (59%): $7,965</li>
            <li><strong>Producer Premium: $5,535</strong></li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Higher coverage levels have LOWER subsidy percentages. Enterprise units have HIGHER subsidies than basic/optional units. This encourages risk management while controlling program costs.</p>
        </div>
      `
    },
    {
      title: "Covered Causes of Loss",
      content: `
        <h2>Covered Causes of Loss</h2>

        <p>MPCI covers a wide range of perils (causes of loss), but not everything is covered. Understanding what is and isn't covered is essential for proper claims handling.</p>

        <h3>Covered Perils</h3>
        <p>MPCI typically covers unavoidable losses due to:</p>
        <ul>
          <li><strong>Drought</strong> - Lack of adequate moisture</li>
          <li><strong>Excess Moisture/Precipitation</strong> - Too much rain, flooding</li>
          <li><strong>Hail</strong> - Damage from hailstorms</li>
          <li><strong>Wind</strong> - Crop damage from high winds</li>
          <li><strong>Frost/Freeze</strong> - Damage from cold temperatures</li>
          <li><strong>Insects</strong> - Damage from insect infestations (with conditions)</li>
          <li><strong>Plant Disease</strong> - Crop diseases (with conditions)</li>
          <li><strong>Wildlife</strong> - Damage from wildlife (with conditions)</li>
          <li><strong>Fire</strong> - Crop destroyed by fire</li>
          <li><strong>Earthquake</strong> - Damage from seismic events</li>
          <li><strong>Volcanic Eruption</strong> - Ash or lava damage</li>
          <li><strong>Failure of Irrigation Water Supply</strong> - Under certain conditions</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Unavoidable Loss Requirement</h3>
          <p>For a loss to be covered, it must be:</p>
          <ul>
            <li>Due to an insured cause of loss</li>
            <li>Unavoidable through good farming practices</li>
            <li>Not caused by the producer's negligence or mismanagement</li>
          </ul>
        </div>

        <h3>Exclusions - NOT Covered</h3>
        <ul>
          <li>Negligence or mismanagement by the producer</li>
          <li>Failure to follow good farming practices</li>
          <li>Losses that could have been avoided</li>
          <li>Theft (not an insured peril)</li>
          <li>Damage done during harvest</li>
          <li>Losses from uninsured causes</li>
          <li>Quality losses (unless specifically covered)</li>
        </ul>

        <div class="definition">
          <h4>Good Farming Practices</h4>
          <p>Production methods that are recognized by agricultural experts for the area. A producer must follow good farming practices to have coverage. Examples include:</p>
          <ul>
            <li>Proper planting dates and methods</li>
            <li>Appropriate pest and weed control</li>
            <li>Adequate fertilization</li>
            <li>Timely harvest</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>The exam may present scenarios asking if a loss is covered. Ask yourself: Was the cause insured? Was the loss unavoidable? Did the producer follow good farming practices? All three must be YES for coverage.</p>
        </div>
      `
    },
    {
      title: "Notice of Loss Requirements",
      content: `
        <h2>Notice of Loss Requirements</h2>

        <p>When a loss occurs, the insured must notify the insurance company within specific timeframes. Failure to provide timely notice can jeopardize the claim.</p>

        <h3>When Notice Must Be Given</h3>

        <div class="key-concept">
          <h3>Key Concept: Notice Deadlines</h3>
          <p>The insured must provide written notice of loss:</p>
          <ul>
            <li><strong>Within 72 hours</strong> of initial discovery of damage (for most claims)</li>
            <li><strong>Within 15 days</strong> of the end of the insurance period</li>
            <li><strong>Before any destroyed acreage is put to another use</strong></li>
            <li><strong>Before harvest</strong> of any damaged production</li>
          </ul>
        </div>

        <h3>What the Notice Must Include</h3>
        <ul>
          <li>Policy number and name of insured</li>
          <li>Date damage was discovered</li>
          <li>Cause of loss (peril)</li>
          <li>Description and location of damaged acreage</li>
          <li>Estimated damage percentage</li>
        </ul>

        <div class="important">
          <h3>Important: Failure to Give Notice</h3>
          <p>If the insured fails to provide timely notice:</p>
          <ul>
            <li>The insurance company may deny the claim</li>
            <li>Coverage may be reduced</li>
            <li>Evidence of damage may be lost, making claim verification impossible</li>
          </ul>
        </div>

        <h3>Pre-Harvest Appraisal</h3>
        <div class="definition">
          <p>A <strong>pre-harvest appraisal</strong> is required if the insured wants to destroy or abandon acreage before harvest. The insured must:</p>
          <ul>
            <li>Give notice of intent to destroy</li>
            <li>Wait for company representative to appraise damage</li>
            <li>Receive written consent before destroying the crop</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>72 hours for initial notice is a key number to remember. Also remember: NEVER destroy a damaged crop without first notifying the company and getting consent.</p>
        </div>
      `
    },
    {
      title: "Duties of the Insured",
      content: `
        <h2>Duties of the Insured</h2>

        <p>The crop insurance policy imposes specific duties on the insured. Failure to fulfill these duties can result in denied claims or voided coverage.</p>

        <h3>Before a Loss</h3>
        <ul>
          <li><strong>Pay premium</strong> by the due date</li>
          <li><strong>Report acreage</strong> accurately and timely</li>
          <li><strong>Report production</strong> by the production reporting date</li>
          <li><strong>Follow good farming practices</strong></li>
          <li><strong>Maintain records</strong> of production, sales, and farming operations</li>
          <li><strong>Protect the crop</strong> from further damage when possible</li>
        </ul>

        <h3>After a Loss</h3>
        <ul>
          <li><strong>Give notice</strong> of damage within 72 hours</li>
          <li><strong>Protect the crop</strong> from further damage</li>
          <li><strong>Not destroy</strong> damaged crop without consent</li>
          <li><strong>Leave representative samples</strong> of unharvested production</li>
          <li><strong>Cooperate</strong> with the adjuster's investigation</li>
          <li><strong>Provide records</strong> and documentation as requested</li>
          <li><strong>Complete harvest</strong> in a timely manner</li>
          <li><strong>Submit claim</strong> information accurately</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Duty to Protect the Crop</h3>
          <p>Even after damage occurs, the insured must take reasonable steps to protect the crop and minimize further losses. This includes:</p>
          <ul>
            <li>Continuing normal farming operations (spraying, irrigation, etc.)</li>
            <li>Harvesting what can be harvested</li>
            <li>Not abandoning the crop without authorization</li>
          </ul>
        </div>

        <div class="important">
          <h3>Important: Record Keeping Requirements</h3>
          <p>The insured must maintain records for at least 3 years after the crop year, including:</p>
          <ul>
            <li>Planting records</li>
            <li>Production records</li>
            <li>Sales records</li>
            <li>Expense records</li>
          </ul>
          <p>These records may be audited by the AIP or RMA at any time.</p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know the duty to protect the crop after loss. An insured cannot simply abandon a damaged crop - they must continue to care for it and harvest what's salvageable.</p>
        </div>
      `
    },
    {
      title: "Prevented Planting Coverage",
      content: `
        <h2>Prevented Planting Coverage</h2>

        <p>Prevented planting coverage provides protection when an insured cause of loss prevents the producer from planting the insured crop.</p>

        <div class="definition">
          <h4>What is Prevented Planting?</h4>
          <p><strong>Prevented planting</strong> occurs when the producer is unable to plant the insured crop by the final planting date (or late planting period) due to an insured cause of loss.</p>
        </div>

        <h3>Eligibility Requirements</h3>
        <ul>
          <li>Must have intended to plant the crop</li>
          <li>Must have been prevented by an insured cause (typically excess moisture/flooding)</li>
          <li>Must have been unable to plant by the final planting date or late planting period</li>
          <li>Must have attempted to plant or had conditions that prevented any attempt</li>
        </ul>

        <h3>Payment Calculation</h3>
        <div class="key-concept">
          <h3>Key Concept: Prevented Planting Payment</h3>
          <p>The prevented planting payment is a percentage of the production guarantee:</p>
          <ul>
            <li><strong>55%</strong> of the guarantee for most crops</li>
            <li><strong>60%</strong> of the guarantee for certain crops (corn, soybeans in some areas)</li>
          </ul>
          <p>Optional higher coverage levels may be purchased for additional premium.</p>
        </div>

        <div class="example">
          <h4>Example: Prevented Planting Payment</h4>
          <p>A producer's corn policy has:</p>
          <ul>
            <li>Guarantee: 112.5 bu/acre (150 APH × 75% coverage)</li>
            <li>Price: $4.00/bu</li>
            <li>Prevented planting acres: 200</li>
            <li>Payment rate: 60%</li>
          </ul>
          <p><strong>Payment = 200 acres × 112.5 bu × $4.00 × 60% = $54,000</strong></p>
        </div>

        <h3>Second Crop Considerations</h3>
        <div class="important">
          <h3>Important: Planting a Second Crop</h3>
          <p>If the producer plants a second (substitute) crop on prevented planting acreage:</p>
          <ul>
            <li>The prevented planting payment is reduced by 35 percentage points</li>
            <li>60% payment becomes 25%</li>
            <li>55% payment becomes 20%</li>
          </ul>
          <p>This discourages planting substitute crops solely to collect prevented planting payments.</p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know the prevented planting payment percentages (55%/60%) and the 35-percentage-point reduction for planting a second crop. These are commonly tested concepts.</p>
        </div>
      `
    },
    {
      title: "Late Planting Provisions",
      content: `
        <h2>Late Planting Provisions</h2>

        <p>When planting is delayed past the final planting date, the late planting period allows coverage to continue at a reduced level.</p>

        <div class="definition">
          <h4>Final Planting Date</h4>
          <p>The last day of the planting period established by RMA for full coverage. Planting after this date reduces the guarantee.</p>
        </div>

        <div class="definition">
          <h4>Late Planting Period</h4>
          <p>A period after the final planting date (typically 25 days) during which the crop can still be planted with reduced coverage.</p>
        </div>

        <h3>Guarantee Reduction for Late Planting</h3>
        <div class="key-concept">
          <h3>Key Concept: Late Planting Reduction</h3>
          <p>For each day planted after the final planting date, the guarantee is reduced by <strong>1% per day</strong> for the first 25 days.</p>
          <ul>
            <li>10 days late = 10% reduction in guarantee</li>
            <li>20 days late = 20% reduction in guarantee</li>
            <li>25 days late = 25% reduction in guarantee (maximum)</li>
          </ul>
        </div>

        <div class="example">
          <h4>Example: Late Planting Impact</h4>
          <p>A producer plants corn 15 days after the final planting date:</p>
          <ul>
            <li>Original guarantee: 112.5 bu/acre</li>
            <li>Late planting reduction: 15%</li>
            <li>Reduced guarantee: 112.5 × 0.85 = <strong>95.6 bu/acre</strong></li>
          </ul>
        </div>

        <h3>Beyond the Late Planting Period</h3>
        <div class="important">
          <h3>Important: Planting After Late Period</h3>
          <p>If the crop is planted after the late planting period ends:</p>
          <ul>
            <li>Coverage may be significantly reduced or denied</li>
            <li>Producer may be eligible for prevented planting instead</li>
            <li>Must notify the insurance company of intent to plant late</li>
          </ul>
        </div>

        <h3>Producer Options at Final Planting Date</h3>
        <ol>
          <li><strong>Plant on time:</strong> Full guarantee applies</li>
          <li><strong>Plant late:</strong> Reduced guarantee (1% per day)</li>
          <li><strong>Claim prevented planting:</strong> Receive 55-60% of guarantee</li>
          <li><strong>Plant substitute crop:</strong> May affect prevented planting payment</li>
        </ol>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Remember: 1% per day reduction for late planting, up to 25% maximum. After the late planting period, significantly different rules apply.</p>
        </div>
      `
    },
    {
      title: "Replanting Provisions",
      content: `
        <h2>Replanting Provisions</h2>

        <p>When a planted crop is damaged and must be replanted, MPCI may provide a replant payment to help cover the additional costs.</p>

        <div class="definition">
          <h4>Replant Payment</h4>
          <p>A payment to compensate the insured for the cost of replanting a damaged crop. This is separate from any indemnity for the crop loss itself.</p>
        </div>

        <h3>Eligibility for Replant Payment</h3>
        <ul>
          <li>Damage must be from an insured cause of loss</li>
          <li>Damage must occur during the insured period</li>
          <li>Remaining stand must be below the level needed for a full yield</li>
          <li>Must be practical to replant</li>
          <li>Must replant to the same crop (usually)</li>
        </ul>

        <h3>Replant Payment Calculation</h3>
        <div class="key-concept">
          <h3>Key Concept: Replant Payment Amount</h3>
          <p>The replant payment covers reasonable costs of replanting, typically calculated as:</p>
          <ul>
            <li>A fixed dollar amount per acre</li>
            <li>The lesser of actual replanting costs or a maximum amount</li>
            <li>Limited to one replant payment per acreage per crop year</li>
          </ul>
        </div>

        <div class="example">
          <h4>Example: Replant Scenario</h4>
          <p>A producer's corn crop is destroyed by hail in early June:</p>
          <ul>
            <li>100 acres affected</li>
            <li>Replant payment rate: $30/acre</li>
            <li>Replant payment: 100 × $30 = $3,000</li>
          </ul>
          <p>The producer receives this payment AND the replanted crop remains insured at the original guarantee (assuming replanting within late planting period).</p>
        </div>

        <h3>When Replanting is NOT Required</h3>
        <div class="important">
          <h3>Important: Producer Choice</h3>
          <p>The insured is generally not required to replant. However:</p>
          <ul>
            <li>If replanting is practical and the producer chooses not to, the original guarantee may be reduced</li>
            <li>The insurance company cannot force replanting but can adjust the claim accordingly</li>
            <li>The producer should discuss options with the adjuster</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>The replant payment is SEPARATE from the indemnity. A producer may receive a replant payment AND still receive an indemnity if the replanted crop also suffers a loss.</p>
        </div>
      `
    },
    {
      title: "Written Agreements",
      content: `
        <h2>Written Agreements</h2>

        <p>Written agreements allow modifications to standard policy terms to accommodate special situations not addressed in the basic provisions.</p>

        <div class="definition">
          <h4>What is a Written Agreement?</h4>
          <p>A <strong>written agreement</strong> is a document that modifies the terms of the crop insurance policy for a specific situation. It must be approved by RMA before it becomes effective.</p>
        </div>

        <h3>Common Uses for Written Agreements</h3>
        <ul>
          <li><strong>Extending coverage</strong> to a crop/county combination not normally insurable</li>
          <li><strong>Establishing yields</strong> for new farming operations</li>
          <li><strong>Modifying practices</strong> (irrigation, organic, etc.)</li>
          <li><strong>Addressing unique situations</strong> not covered by standard provisions</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Written Agreement Process</h3>
          <ol>
            <li>Producer identifies need for non-standard terms</li>
            <li>Agent/AIP submits request to RMA with supporting documentation</li>
            <li>RMA reviews and approves, denies, or modifies the request</li>
            <li>If approved, agreement is attached to the policy</li>
            <li>Must be renewed annually (not automatic)</li>
          </ol>
        </div>

        <h3>Important Considerations</h3>
        <ul>
          <li>Written agreements must be requested BEFORE the sales closing date</li>
          <li>Processing takes time - submit requests early</li>
          <li>May require additional premium</li>
          <li>Terms may differ from standard policy provisions</li>
        </ul>

        <div class="important">
          <h3>Important: Limitations</h3>
          <p>Written agreements cannot:</p>
          <ul>
            <li>Provide coverage that is prohibited by law or regulation</li>
            <li>Modify basic policy provisions without actuarial justification</li>
            <li>Be issued after the sales closing date (with limited exceptions)</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Written agreements are for special situations not covered by standard policy provisions. They require RMA approval and must be requested before the sales closing date.</p>
        </div>
      `
    },
    {
      title: "Policy Cancellation and Termination",
      content: `
        <h2>Policy Cancellation and Termination</h2>

        <p>Understanding how and when policies can be cancelled or terminated is important for both agents and producers.</p>

        <h3>Continuous Policy</h3>
        <div class="definition">
          <p>MPCI policies are <strong>continuous</strong>, meaning they remain in effect from year to year unless cancelled by the insured or terminated by the insurance company.</p>
        </div>

        <h3>Cancellation by the Insured</h3>
        <ul>
          <li>Must be done in writing</li>
          <li>Must be received by the sales closing date</li>
          <li>Effective at the end of the current crop year</li>
          <li>No cancellation mid-season for most crops</li>
        </ul>

        <h3>Termination by the Company</h3>
        <div class="key-concept">
          <h3>Key Concept: Grounds for Termination</h3>
          <p>The insurance company may terminate a policy for:</p>
          <ul>
            <li><strong>Non-payment of premium:</strong> If premium is not paid by the due date</li>
            <li><strong>Debt to FCIC:</strong> Outstanding debt from prior crop years</li>
            <li><strong>Material misrepresentation:</strong> False information on the application</li>
            <li><strong>Fraud:</strong> Intentional deception</li>
            <li><strong>Ineligibility:</strong> Loss of eligibility status</li>
          </ul>
        </div>

        <h3>Void Policies</h3>
        <div class="definition">
          <p>A policy may be declared <strong>void</strong> (as if it never existed) if:</p>
          <ul>
            <li>The insured was never eligible for coverage</li>
            <li>Material misrepresentation was made at application</li>
            <li>Fraud is discovered</li>
          </ul>
        </div>

        <div class="important">
          <h3>Important: Consequences of Termination</h3>
          <ul>
            <li>Premium debt is owed regardless of termination</li>
            <li>May affect eligibility for future coverage</li>
            <li>May affect eligibility for other USDA programs</li>
            <li>Termination for cause may result in sanctions</li>
          </ul>
        </div>

        <h3>Transfer of Coverage</h3>
        <p>When land ownership or lease changes:</p>
        <ul>
          <li>Coverage generally follows the insured, not the land</li>
          <li>New operators must obtain their own coverage</li>
          <li>APH may transfer under certain conditions</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>MPCI policies are continuous - they auto-renew unless cancelled by the sales closing date. Know the difference between cancellation (by insured) and termination (by company).</p>
        </div>
      `
    }
  ],
  quizQuestions: [
    {
      question: "What does MPCI stand for?",
      options: [
        "Multi-Peril Crop Insurance",
        "Maximum Production Coverage Insurance",
        "Multiple Premium Crop Indemnity",
        "Managed Producer Crop Insurance"
      ],
      correct: 0,
      explanation: "MPCI stands for Multi-Peril Crop Insurance, meaning it covers losses from multiple causes (perils) like drought, hail, frost, and disease."
    },
    {
      question: "Which organization administers the federal crop insurance program?",
      options: [
        "National Crop Insurance Services (NCIS)",
        "State Department of Agriculture",
        "Risk Management Agency (RMA)",
        "Farm Service Agency (FSA)"
      ],
      correct: 2,
      explanation: "The Risk Management Agency (RMA) of the USDA administers federal crop insurance programs. NCIS handles private crop hail insurance."
    },
    {
      question: "The Federal Crop Insurance Act of 1980 is significant because it:",
      options: [
        "Eliminated all crop insurance programs",
        "Created the public-private partnership with AIPs and premium subsidies",
        "Made crop insurance mandatory for all farmers",
        "Transferred crop insurance to state control"
      ],
      correct: 1,
      explanation: "The 1980 Act created the modern public-private partnership system with Approved Insurance Providers (AIPs) delivering policies and government-subsidized premiums."
    },
    {
      question: "What is the primary difference between Revenue Protection (RP) and RP-HPE?",
      options: [
        "RP covers only yield losses while RP-HPE covers revenue",
        "RP-HPE has higher premiums than standard RP",
        "RP guarantee can increase if harvest price rises; RP-HPE cannot",
        "RP-HPE is only available for row crops"
      ],
      correct: 2,
      explanation: "With standard RP, the guarantee can increase if the harvest price exceeds the projected price. RP-HPE (Harvest Price Exclusion) does not have this feature, resulting in lower premiums."
    },
    {
      question: "The minimum number of years of production history needed for an APH database is:",
      options: [
        "2 years",
        "4 years",
        "6 years",
        "10 years"
      ],
      correct: 1,
      explanation: "A minimum of 4 years of production history is required for an APH database. If fewer years are available, Transitional Yields (T-Yields) are used to complete the database."
    },
    {
      question: "What are Transitional Yields (T-Yields)?",
      options: [
        "Yields used during the transition from one AIP to another",
        "County-based yields used when producer lacks sufficient history",
        "Yields adjusted for changes in farming practices",
        "Maximum yields allowed for new producers"
      ],
      correct: 1,
      explanation: "T-Yields are county-based yields established by RMA that are used when a producer doesn't have enough years of production history to complete their APH database."
    },
    {
      question: "Which unit type provides the lowest premium but requires loss across the entire unit to collect?",
      options: [
        "Basic Unit",
        "Optional Unit",
        "Enterprise Unit",
        "Section Unit"
      ],
      correct: 2,
      explanation: "Enterprise units combine all acreage of a crop in a county into one unit. They have the lowest premiums but require the loss calculation to be done across all combined acreage."
    },
    {
      question: "The Sales Closing Date (SCD) is:",
      options: [
        "The last day to harvest the insured crop",
        "The deadline to report production",
        "The last day to apply for, change, or cancel coverage",
        "The date when premium is due"
      ],
      correct: 2,
      explanation: "The Sales Closing Date is the last date to apply for new coverage, make changes to existing coverage, or cancel a policy for the upcoming crop year."
    },
    {
      question: "If a producer reports 500 acres but actually planted 550 acres, what happens to the guarantee?",
      options: [
        "Guarantee remains unchanged",
        "Guarantee is reduced proportionally (500/550 = 90.9%)",
        "Policy is voided",
        "Producer receives bonus coverage"
      ],
      correct: 1,
      explanation: "Under-reporting acreage results in a proportional reduction of the guarantee. The guarantee is adjusted by multiplying by the ratio of reported to determined acres (500÷550=90.9%)."
    },
    {
      question: "Premium subsidies for MPCI are:",
      options: [
        "Higher at higher coverage levels",
        "The same regardless of coverage level",
        "Lower at higher coverage levels",
        "Only available for enterprise units"
      ],
      correct: 2,
      explanation: "Premium subsidy percentages decrease as coverage levels increase. For example, 50% coverage may receive 67% subsidy while 85% coverage may receive only 55% subsidy."
    },
    {
      question: "Which of the following is NOT a covered cause of loss under MPCI?",
      options: [
        "Drought",
        "Theft",
        "Hail",
        "Frost"
      ],
      correct: 1,
      explanation: "Theft is not a covered cause of loss under MPCI. Covered perils include drought, excess moisture, hail, frost, wind, insects, disease, wildlife, and fire."
    },
    {
      question: "The insured must give notice of loss within:",
      options: [
        "24 hours of discovery",
        "72 hours of initial discovery",
        "7 days of discovery",
        "30 days of discovery"
      ],
      correct: 1,
      explanation: "The insured must provide written notice of damage within 72 hours of initial discovery. This allows the insurance company to inspect damage while evidence is still available."
    },
    {
      question: "Before destroying a damaged crop, the insured must:",
      options: [
        "Wait 30 days after the loss",
        "Notify the insurance company and receive written consent",
        "File a claim form",
        "Pay the deductible"
      ],
      correct: 1,
      explanation: "The insured must notify the insurance company and receive written consent before destroying or abandoning any damaged acreage. This allows for proper loss appraisal."
    },
    {
      question: "The standard prevented planting payment is what percentage of the guarantee?",
      options: [
        "25-30%",
        "40-45%",
        "55-60%",
        "75-80%"
      ],
      correct: 2,
      explanation: "The standard prevented planting payment is 55% of the guarantee for most crops, or 60% for certain crops like corn and soybeans in some areas."
    },
    {
      question: "If a producer plants a second crop on prevented planting acres, the payment is:",
      options: [
        "Increased by 35 percentage points",
        "Reduced by 35 percentage points",
        "Eliminated entirely",
        "Unchanged"
      ],
      correct: 1,
      explanation: "Planting a second crop on prevented planting acreage reduces the payment by 35 percentage points (e.g., 60% becomes 25%, 55% becomes 20%)."
    },
    {
      question: "For late planting, the guarantee is reduced by:",
      options: [
        "1% per day up to 25 days",
        "2% per day up to 20 days",
        "5% per week up to 4 weeks",
        "A flat 15% regardless of days late"
      ],
      correct: 0,
      explanation: "For each day planted after the final planting date, the guarantee is reduced by 1% per day for the first 25 days of the late planting period."
    },
    {
      question: "A replant payment is:",
      options: [
        "Part of the indemnity payment",
        "Separate from and in addition to any indemnity",
        "Only available if no indemnity is paid",
        "Equal to the total premium paid"
      ],
      correct: 1,
      explanation: "The replant payment is separate from the indemnity. A producer may receive a replant payment to cover replanting costs AND still receive an indemnity if the replanted crop suffers a covered loss."
    },
    {
      question: "Written agreements must be requested:",
      options: [
        "After the crop is planted",
        "At the time of loss",
        "Before the sales closing date",
        "Within 30 days of harvest"
      ],
      correct: 2,
      explanation: "Written agreements must be requested before the sales closing date. They require RMA approval and cannot generally be issued after the deadline."
    },
    {
      question: "MPCI policies are described as 'continuous' meaning they:",
      options: [
        "Never have coverage gaps",
        "Cover crops continuously throughout the year",
        "Remain in effect year to year unless cancelled",
        "Can be cancelled at any time"
      ],
      correct: 2,
      explanation: "Continuous policies automatically renew for the next crop year unless the insured cancels by the sales closing date. The producer doesn't need to re-apply annually."
    },
    {
      question: "An Approved Insurance Provider (AIP) is:",
      options: [
        "A government agency that sets crop insurance rates",
        "A private company authorized to sell federal crop insurance",
        "A producer organization that lobbies for crop insurance",
        "A reinsurance company that backs FCIC"
      ],
      correct: 1,
      explanation: "AIPs are private insurance companies authorized to sell and service federal crop insurance policies under the Standard Reinsurance Agreement with FCIC."
    },
    {
      question: "What is the role of the Federal Crop Insurance Corporation (FCIC)?",
      options: [
        "Sells policies directly to farmers",
        "Develops policies, sets rates, provides subsidies and reinsurance",
        "Only provides claims adjustment services",
        "Regulates private crop hail insurance"
      ],
      correct: 1,
      explanation: "FCIC develops and approves crop insurance policies, sets premium rates, provides premium subsidies, and offers reinsurance to AIPs. It does not sell directly to farmers."
    },
    {
      question: "CAT (Catastrophic) coverage provides:",
      options: [
        "100% of approved yield at 100% of price",
        "75% of approved yield at 75% of price",
        "50% of approved yield at 55% of price",
        "25% of approved yield at 25% of price"
      ],
      correct: 2,
      explanation: "CAT coverage is the minimum level, providing 50% of the approved yield at 55% of the price. The producer pays only an administrative fee, not a premium."
    },
    {
      question: "Yield Protection (YP) insures against:",
      options: [
        "Price decline only",
        "Production losses only",
        "Both yield and price losses",
        "Revenue losses only"
      ],
      correct: 1,
      explanation: "Yield Protection covers production (yield) losses only. The guarantee is based on yield, and the price component is fixed at the projected price."
    },
    {
      question: "Which coverage level range is typically available above CAT for MPCI?",
      options: [
        "25% to 50%",
        "50% to 85%",
        "75% to 100%",
        "55% to 95%"
      ],
      correct: 1,
      explanation: "Above CAT level, producers can typically select coverage levels from 50% to 85% of their approved yield or revenue, usually in 5% increments."
    },
    {
      question: "To have a covered loss under MPCI, the loss must be:",
      options: [
        "From any cause whatsoever",
        "From an insured peril and unavoidable through good farming practices",
        "Reported within 24 hours",
        "Greater than 50% of expected yield"
      ],
      correct: 1,
      explanation: "For coverage, the loss must be from an insured cause of loss and must be unavoidable through good farming practices. Losses due to negligence are not covered."
    },
    {
      question: "Good farming practices include all EXCEPT:",
      options: [
        "Proper planting dates and methods",
        "Appropriate pest and weed control",
        "Abandoning a damaged crop without authorization",
        "Timely harvest"
      ],
      correct: 2,
      explanation: "Good farming practices include proper planting, pest control, and timely harvest. Abandoning a damaged crop without authorization violates the duty to protect the crop."
    },
    {
      question: "The Acreage Reporting Date (ARD) is the deadline to:",
      options: [
        "Report all planted acreage for the crop year",
        "Pay the premium",
        "File a claim",
        "Cancel the policy"
      ],
      correct: 0,
      explanation: "The ARD is the deadline to report all planted acreage, including crop type, planting dates, shares, and practice (irrigated/non-irrigated)."
    },
    {
      question: "Enterprise units offer which benefit compared to optional units?",
      options: [
        "Higher guarantee levels",
        "Lower premium through significant discount",
        "Payment for localized losses",
        "No recordkeeping requirements"
      ],
      correct: 1,
      explanation: "Enterprise units provide a significant premium discount (typically 15-20%) because all acreage is combined into one unit. The trade-off is that localized losses may not trigger payment."
    },
    {
      question: "What happens if a producer over-reports acreage?",
      options: [
        "The guarantee is increased",
        "The policy is adjusted to actual acres only",
        "The policy is voided",
        "The producer receives a bonus payment"
      ],
      correct: 1,
      explanation: "If reported acreage exceeds actual acreage, the policy is simply adjusted to the actual acres. The producer does not benefit from over-reporting."
    },
    {
      question: "The Production Reporting Date (PRD) is when:",
      options: [
        "Production records from the prior year must be submitted",
        "The estimated yield for the current year must be reported",
        "Harvest begins",
        "The premium is calculated"
      ],
      correct: 0,
      explanation: "The PRD is the deadline to submit the previous year's production records to update the APH database for the current policy year."
    }
  ]
};

export default chapter3;
